import { MagnifyingGlass } from '@creditornot/cb-icons';
import { cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { equals } from 'ramda';
import React, { ReactNode, useContext, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';
import styled, { useTheme } from 'styled-components';

import { usePrevious, useResizeObserver } from 'common/hooks';
import LazyLoadedThumbnail from 'modules/menu/components/LazyLoadedThumbnail';

import EmptyList from './EmptyList';
import MenuEditorListRowRenderer, { RowRendererData } from './MenuEditorListRowRenderer';
import { VenueIsLinkedAlert } from './VenueIsLinkedAlert';
import { MenuLocaleContext } from '../MenuLocaleContext';
import { MENU_MOBILE_BREAKPOINT } from '../constants';
import { useSharedMenuLink } from '../hooks/useSharedMenuLink';
import CollectionListErrorBoundary from '../items/components/CollectionListErrorBoundary';

type Props = {
  className?: string;
  title: string;
  image?: {
    src: string;
    blurhash: string | null;
  };
  description: string;
  search: string;
  onSearch: (search: string) => void;
  headerRight?: ReactNode;
  toolbar?: ReactNode;
  itemData: RowRendererData;
  children: ReactNode;
};

const Root = styled.div`
  height: 100%;
  display: flex;
`;

const CollectionContainer = styled.div`
  flex: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const HeaderContainer = styled.div`
  width: 100%;
  min-height: 104px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0 24px;
  overflow: hidden;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    justify-content: center;
    align-items: initial;
    padding: 0 16px;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  white-space: nowrap;
  overflow: hidden;
  user-select: text;
`;

const Title = styled.div`
  ${typographyCss.Heading4};
  color: ${cbSemanticCssVars.textPrimary};
  margin-block-end: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ThumbnailContainer = styled.div`
  margin-inline-end: 16px;

  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const Description = styled.div`
  ${typographyCss.Body3};
  color: ${cbSemanticCssVars.textPrimary};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HeaderLeft = styled.div`
  display: flex;
  white-space: nowrap;
  overflow: hidden;
  align-items: center;
  margin-inline-end: 8px;
`;

const HeaderRight = styled.div`
  display: flex;
`;

const StyledInput = styled(Input)<{ hasMargin: boolean }>`
  margin-inline-end: ${({ hasMargin }) => (hasMargin ? '16px' : 0)};
  min-width: 202px;
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    margin-inline-end: ${({ hasMargin }) => (hasMargin ? '8px' : 0)};
    min-width: 160px;
  }
`;

const ToolbarContainer = styled.div`
  min-height: 64px;
  overflow: auto;
`;

const ContentContainer = styled.div`
  min-width: 0;
`;

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const ListWrapper = styled.div`
  height: 100%;
`;

const useVirtualListHeights = (itemData: RowRendererData) => {
  const listRef = useRef<VariableSizeList>(null);

  const virtualListHeights = useMemo(
    () =>
      itemData.list.map(el =>
        el.type === 'category-subheader' || el.type === 'options-subheader' || el.type === 'subcategory-subheader'
          ? 64
          : 105,
      ),
    [itemData.list],
  );
  const previousVirtualListHeights = usePrevious(virtualListHeights);

  useEffect(() => {
    if (!equals(virtualListHeights, previousVirtualListHeights)) {
      /* Forces recalculating heights after dragging items around */
      listRef.current?.resetAfterIndex(0);
    }
  }, [previousVirtualListHeights, virtualListHeights]);

  return {
    virtualListHeights,
    listRef,
  };
};

const useFocusInputOnCtrlF = () => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.code === 'KeyF') {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return { inputRef };
};

const useIsNarrowView = () => {
  const headerRef = useRef<HTMLDivElement>(null);
  const { width } = useResizeObserver(headerRef);
  // /* 24px account for the left navigation when collapsed */
  const isNarrowView = useMemo(() => width !== undefined && width <= MENU_MOBILE_BREAKPOINT - 24, [width]);

  return { headerRef, isNarrowView };
};

const getItemKey = (index: number, data: RowRendererData) => {
  const row = data.list[index];

  if (row.type === 'item') {
    return row.itemState.id;
  }
  if (row.type === 'option') {
    return row.optionState.id;
  }
  if (row.type === 'options-subheader') {
    return `row-options-subheader-${index}`;
  }
  return row.category ? `row-category-${row.category.id}` : `row-category-${index}`;
};

const MenuEditorCollectionList = ({
  className,
  title,
  image,
  description,
  search,
  onSearch,
  headerRight,
  toolbar,
  children,
  itemData,
}: Props) => {
  const { listRef, virtualListHeights } = useVirtualListHeights(itemData);
  const { inputRef } = useFocusInputOnCtrlF();
  const { headerRef, isNarrowView } = useIsNarrowView();
  const intl = useIntl();
  const { cbDirection } = useTheme();
  const { primaryLanguage } = useContext(MenuLocaleContext);
  const { sharedMenuLink } = useSharedMenuLink();

  return (
    <Root className={className}>
      <CollectionContainer>
        <HeaderContainer ref={headerRef}>
          <HeaderLeft dir="auto" lang={primaryLanguage}>
            {!isNarrowView && image && (
              <ThumbnailContainer>
                <LazyLoadedThumbnail alt={title} blurhash={image.blurhash} src={image.src} />
              </ThumbnailContainer>
            )}
            <TitleContainer>
              <Title title={title}>{title}</Title>
              {!isNarrowView && <Description title={description}>{description}</Description>}
            </TitleContainer>
          </HeaderLeft>
          <HeaderRight>
            <StyledInput
              hasMargin={!!headerRight}
              inputRef={inputRef}
              placeholder={intl.formatMessage({ id: 'menu.searchInCategory' }, { category: title })}
              icon={<MagnifyingGlass />}
              value={search}
              onChange={event => onSearch(event.currentTarget.value)}
            />
            {headerRight}
          </HeaderRight>
        </HeaderContainer>
        {!!sharedMenuLink && <VenueIsLinkedAlert />}
        <ListContainer>
          <CollectionListErrorBoundary>
            {toolbar && <ToolbarContainer>{toolbar}</ToolbarContainer>}
            <ListWrapper>
              <AutoSizer disableWidth>
                {({ height }) =>
                  itemData.list.length > 0 ? (
                    <SortableContext
                      items={itemData.list.map((_, index) => getItemKey(index, itemData))}
                      strategy={verticalListSortingStrategy}
                    >
                      <VariableSizeList<RowRendererData>
                        ref={listRef}
                        style={{ paddingBlockEnd: 105, direction: cbDirection }}
                        height={height}
                        width="100%"
                        itemData={itemData}
                        itemCount={itemData.list.length}
                        itemSize={index => virtualListHeights[index]}
                        itemKey={getItemKey}
                        overscanCount={6}
                      >
                        {MenuEditorListRowRenderer}
                      </VariableSizeList>
                    </SortableContext>
                  ) : (
                    <EmptyList style={{ height }} />
                  )
                }
              </AutoSizer>
            </ListWrapper>
          </CollectionListErrorBoundary>
        </ListContainer>
      </CollectionContainer>
      <ContentContainer>{children}</ContentContainer>
    </Root>
  );
};

export default React.memo(MenuEditorCollectionList);
